// The full-page table. This file is the shell: it validates and
// normalizes the configuration, owns the state, takes the visible rows from the source, provides TableContext
// and composes the slots. Every part of the table lives in its own module and reads the table through
// TableContext; the shapes they share are in types.js. Styles: base_app/styles/global/z5-me-table.css,
// z6-me-table-*.css and z5-me-components.css.
//
// The source is set by config.source.mode. Client-backed: the consumer owns the complete set; pass `rows`
// with `status` 'loading' | 'error' | 'ready' and an `onRetry`. Page-backed: pass `load(request, {signal})`,
// which resolves to one page and its exact counts; the table owns the request and its status.

import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import PropTypes from 'prop-types'

import {
  identityColumn,
  normalizeConfig,
  useDuplicateTableId,
  validateConfig,
  validateRows,
} from './config'
import { TableContext } from './context'
import { activeFilters, exportRows } from './query'
import { ACTIONS } from './types'
import { useBreakpoint } from './useBreakpoint'
import { useTableState } from './state/useTableState'
import { useClientSource } from './source/client'
import { usePageSource } from './source/page'
import { requestOf } from './source/request'
import { useGeometry } from './geometry/useGeometry'
import { ColGroup, tableMinWidth, tableWidth } from './geometry/ColGroup'
import { ProxyScrollbar } from './geometry/ProxyScrollbar'
import { Header } from './layout/Header'
import { ConsumerRegion } from './layout/ConsumerRegion'
import { actionShape } from './layout/PageActions'
import { Toolbar } from './layout/Toolbar'
import { ResetRow } from './layout/ResetRow'
import { useSearchDebounce } from './layout/Search'
import { Caption } from './headers/Caption'
import { HeaderRow } from './headers/HeaderRow'
import { FilterBar } from './filters/FilterBar'
import { TopPager } from './pager/Pagers'
import { Footer } from './pager/Footer'
import { ViewOptions } from './view/ViewOptions'
import { ResizeBoundary } from './view/ResizeBoundary'
import { reconcileView, viewColumns } from './view/view'
import { Cell } from './cells/registry'
import { RowActions } from './cells/RowActions'
import { SummaryRow } from './cells/SummaryRow'
import { HeaderCheckbox } from './selection/HeaderCheckbox'
import { SelectCell } from './selection/SelectCell'
import { BulkBar } from './selection/BulkBar'
import { rowActionStateFor } from './selection/conflicts'
import { isSelected } from './selection/selection'
import { useSelectionWork } from './selection/useSelection'
import { BodyRows } from './rows/BodyRow'
import { ExpansionWatch } from './rows/ExpansionWatch'
import { StateSurface } from './states/StateSurface'
import { SkeletonRows } from './states/SkeletonRows'
import { focusAfterFrame } from './states/focusTarget'
import { GroupedTable } from './grouped/GroupedTable'
import { useGroupExpansion } from './grouped/useGroupExpansion'

export { SEARCH_DEBOUNCE_MS } from './layout/Search'

/** @type {import('./types').Slots} */
const SLOTS = {
  ConsumerRegion,
  FilterBar,
  ViewOptions,
  TopPager,
  HeaderCheckbox,
  SelectCell,
  ResizeBoundary,
  ColGroup,
  ProxyScrollbar,
  BulkBar,
  RowActions,
  Cell,
}

// a grouped instance renders no pager; its footer carries only the result count
const NoPager = () => null
const GROUPED_SLOTS = { ...SLOTS, TopPager: NoPager }

const NO_ERRORS = []
const NO_ROWS = []

// the unit's element id: stable per tableId, so the bulk bar's stack can name the unit as its content region
export const unitIdFor = tableId =>
  `me-table-content-${String(tableId || '').replace(/[^\w-]/g, '-')}`

// actions that change the committed query, page or page size, so a page-backed source requests again
const REQUESTING = new Set([
  ACTIONS.searchCommit,
  ACTIONS.filterSet,
  ACTIONS.queryClear,
  ACTIONS.sortToggle,
  ACTIONS.pageSet,
  ACTIONS.pageSizeSet,
])

// the control the person used; focus returns to it once the request it started settles
const fromControl = element => ({
  element:
    element && element !== document.body && element.isConnected
      ? element
      : null,
  userInitiated: true,
})

const ConfigError = ({ errors }) => (
  <div className="me-table-unit">
    <div className="me-table-config-error" role="alert">
      <h2>Table configuration error</h2>
      <ul>
        {errors.map(error => (
          <li key={error}>{error}</li>
        ))}
      </ul>
    </div>
  </div>
)

ConfigError.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.string).isRequired,
}

const bodyKind = (status, derived) => {
  if (derived.busy) return 'loading'
  if (status === 'error') return 'error'
  if (derived.zeroData) return 'zero-data'
  if (derived.zeroResults) return 'zero-results'
  return 'rows'
}

export const MeTable = ({
  config: rawConfig,
  rows = [],
  status: clientStatus = 'loading',
  onRetry = () => {},
  load = null,
  controlledState = null,
  rowActions = null,
  renderDetail = null,
  childRows = null,
  headerActions = null,
  consumerRegion = null,
  onStateChange = null,
}) => {
  const errors = useMemo(() => validateConfig(rawConfig), [rawConfig])
  const config = useMemo(() => normalizeConfig(rawConfig), [rawConfig])
  const duplicateId = useDuplicateTableId(config.tableId)
  const pageMode = config.source.mode === 'page'
  const { columns } = config
  const shellErrors =
    pageMode && typeof load !== 'function'
      ? ['load: a page-backed source supplies load(request, {signal})']
      : NO_ERRORS

  // the source derives the eligible count after the state hook runs, so publication reads it through a ref;
  // a count a bulk validation reported replaces it while the selection stands for that count
  const unitRef = useRef(null)
  // each expanded row's loaded child rows, kept so reopening a row does not reload it
  const childCache = useRef(new Map())
  const work = useSelectionWork(unitRef)
  const eligibleTotal = useRef(null)
  const readChanged = work.changedTotal
  const readEligible = useCallback(() => {
    const changed = readChanged()
    return changed === null ? eligibleTotal.current : changed
  }, [readChanged])
  const [state, apply, read, superseded] = useTableState({
    config,
    onStateChange,
    controlledState,
    eligibleTotal: readEligible,
  })

  // the control that started the current request, with the replacement count it started under; a
  // controlled or configuration change that replaces the request ends its claim on focus
  const [owned, setOwned] = useState(null)
  const generation = useRef(superseded)
  generation.current = superseded
  const setInitiator = useCallback(
    control =>
      setOwned(control ? { control, generation: generation.current } : null),
    []
  )
  const initiator =
    owned && owned.generation === superseded ? owned.control : null
  const dispatch = useCallback(
    action => {
      const requesting =
        pageMode &&
        REQUESTING.has(action.type) &&
        action.reason !== 'response' &&
        action.reason !== 'controlled'
      if (!requesting) {
        apply(action)
        return
      }
      // only an action that changes the request starts one, so only that one records its control
      const element = document.activeElement
      const before = JSON.stringify(requestOf(read(), config))
      apply(action)
      if (JSON.stringify(requestOf(read(), config)) !== before)
        setInitiator(fromControl(element))
    },
    [apply, read, config, pageMode, setInitiator]
  )

  const { narrow } = useBreakpoint()
  const searchHandlers = useSearchDebounce(state, dispatch)

  const titleRef = useRef(null)
  const titleId = useId()
  const captionId = useId()
  const detailPrefix = useId()

  const rowErrors = useMemo(
    () =>
      !pageMode && !errors.length && clientStatus === 'ready'
        ? validateRows(rawConfig, rows)
        : NO_ERRORS,
    [pageMode, rawConfig, rows, clientStatus, errors]
  )
  const clientSource = useClientSource({
    rows: rowErrors.length ? NO_ROWS : rows,
    status: clientStatus,
    columns,
    state,
    selection: config.selection,
    onRetry,
  })
  const pageSource = usePageSource({
    config,
    state,
    dispatch: apply,
    load,
    current: read,
    enabled: pageMode && !errors.length && !shellErrors.length && !duplicateId,
  })
  const source = pageMode ? pageSource : clientSource
  eligibleTotal.current = source.eligibleTotal
  const sourceErrors = pageMode ? pageSource.errors || NO_ERRORS : rowErrors
  const { status } = source
  const ready = status === 'ready'
  const busy = status === 'loading'

  // a client set that no longer reaches the current page moves to its final valid page once it has loaded;
  // a page-backed source corrects its own page from the response's count
  useEffect(() => {
    if (!pageMode && ready && state.page !== source.currentPage)
      apply({
        type: ACTIONS.pageClamp,
        page: source.currentPage,
        reason: 'response',
      })
  }, [pageMode, ready, state.page, source.currentPage, apply])

  // the initiator holds until its request settles; the skeleton rows return focus as they leave
  useEffect(() => {
    if (!busy) setInitiator(null)
  }, [busy, setInitiator])

  const retry = () => {
    setInitiator(fromControl(document.activeElement))
    source.retry()
  }

  // one commit clears search and every filter; the control that did it is gone, so the title takes focus,
  // at once for a client set and once the request settles for a page-backed one
  const clearQuery = () => {
    searchHandlers.cancel()
    if (pageMode) setInitiator(fromControl(titleRef.current))
    apply({ type: ACTIONS.queryClear, reason: 'reset' })
    if (!pageMode) focusAfterFrame(titleRef)
  }

  // a grouped instance's sections open and close without changing its rows, so their state moves the table
  const grouped = Boolean(config.grouped)
  const queryActive =
    Boolean(state.query.search) || activeFilters(state.query.filters).length > 0
  const groups = useGroupExpansion(config.grouped, state.query, queryActive)
  const groupToken = grouped ? groups.token : null

  const geometry = useGeometry(
    unitRef,
    [status, source.pageRows.length, groupToken],
    [
      status,
      source.pageRows,
      source.aggregates,
      state.view,
      columns,
      groupToken,
    ]
  )
  const [toolbarFreeWidth, setToolbarFreeWidth] = useState(0)
  // the toolbar reports after every layout; an unchanged width schedules nothing, so a render that only
  // re-measures can never feed itself another render
  const freeWidth = useRef(0)
  const onFreeWidth = useCallback(width => {
    if (freeWidth.current === width) return
    freeWidth.current = width
    setToolbarFreeWidth(width)
  }, [])

  // row mutations conflict with an active selection and with bulk work still running
  const rowActionState = useMemo(
    () => rowActionStateFor(state.selection, work.pending),
    [state.selection, work.pending]
  )
  const selectionView = useMemo(
    () => ({
      isSelected: (id, row) => isSelected(config, state.selection, id, row),
    }),
    [config, state.selection]
  )

  const allErrors = errors.concat(
    shellErrors,
    sourceErrors,
    duplicateId ? [`tableId: duplicate ${config.tableId} on this page`] : []
  )
  if (allErrors.length) return <ConfigError errors={allErrors} />

  const { search, filters } = state.query
  const unitId = unitIdFor(config.tableId)
  const view = reconcileView(state.view, columns, config.defaultDensity)
  const shown = viewColumns(columns, view)
  const hasActions = Boolean(rowActions)
  const expandable = Boolean(renderDetail || childRows)
  const selectable = Boolean(config.selection)
  const total = ready ? source.total || 0 : 0
  const hasQuery = Boolean(search) || activeFilters(filters).length > 0
  // a grouped instance shows zero data only when it declares no groups; a declared group without rows shows
  // its own empty-group row
  const zeroData =
    ready &&
    (grouped
      ? config.grouped.groups.length === 0
      : pageMode
      ? total === 0 && !hasQuery
      : source.data.length === 0)

  /** @type {import('./types').Derived} */
  const derived = {
    data: source.data,
    matching: source.matching,
    // a grouped instance has no pages: every matching row shows, across its sections
    pageRows: grouped ? source.matching : source.pageRows,
    total,
    eligibleTotal: source.eligibleTotal,
    currentPage: source.currentPage,
    firstShown: grouped ? Math.min(1, total) : source.firstShown,
    lastShown: grouped ? total : source.lastShown,
    totals: source.aggregates,
    hasQuery,
    busy,
    zeroData,
    zeroResults: ready && !zeroData && total === 0 && (hasQuery || !grouped),
  }

  /** @type {import('./types').Layout} */
  const layout = {
    columns: shown,
    idColumn: identityColumn(config),
    columnCount:
      shown.length +
      (hasActions ? 1 : 0) +
      (expandable ? 1 : 0) +
      (selectable ? 1 : 0),
    expandable,
    hasActions,
    selectable,
    searchable: columns.some(column => column.searchable),
    filterColumns: columns.filter(column => column.filterable),
    // group totals belong in the group header, so a grouped instance has no summary row
    showSummary: !grouped && Boolean(config.summary) && ready && total > 0,
    narrow,
  }

  /** @type {import('./types').TableContextValue} */
  const context = {
    config,
    status,
    state,
    dispatch,
    derived,
    layout,
    geometry: { ...geometry, toolbarFreeWidth },
    slots: grouped ? GROUPED_SLOTS : SLOTS,
    refs: { unit: unitRef, title: titleRef },
    unitId,
    titleId,
    captionId,
    detailPrefix,
    search: searchHandlers,
    retry,
    clearQuery,
    exportRows: pageMode
      ? () => []
      : (from = state) =>
          exportRows(source.data, columns, from, undefined, config.grouped),
    rowActionState,
    selection: selectionView,
    rowActions,
    renderDetail,
    childRows,
    childCache: childCache.current,
    headerActions:
      headerActions || (config.pageActions.length ? config.pageActions : null),
    consumerRegion,
    initiator,
    groups,
  }

  const kind = bodyKind(status, derived)
  let body
  if (kind === 'loading') body = <SkeletonRows />
  else if (kind === 'rows') body = <BodyRows />
  else body = <StateSurface kind={kind} />

  return (
    <TableContext.Provider value={context}>
      <section
        ref={unitRef}
        id={unitId}
        className={`me-table-unit${geometry.pinned ? ' is-pinned' : ''}`}
        style={{ '--me-table-sticky-top': `${geometry.stickyTop}px` }}
        data-table-id={config.tableId}
        data-density={view.density}
        aria-labelledby={config.embedded ? captionId : titleId}
      >
        {!config.embedded && <Header />}
        {!config.embedded && !zeroData && <Toolbar onFreeWidth={onFreeWidth} />}
        {!config.embedded && !zeroData && hasQuery && <ResetRow />}
        <div className="me-table-region">
          {config.grouped ? (
            <GroupedTable kind={kind} />
          ) : (
            <table
              className="me-table"
              style={{
                width: `${tableWidth(context)}px`,
                minWidth: `${tableMinWidth(context)}px`,
              }}
              aria-busy={busy ? 'true' : undefined}
            >
              <Caption />
              <ColGroup />
              <HeaderRow />
              <tbody>{body}</tbody>
              <SummaryRow />
            </table>
          )}
        </div>
        <ProxyScrollbar />
        {!zeroData && <Footer />}
        <BulkBar />
        <ExpansionWatch />
      </section>
    </TableContext.Provider>
  )
}

MeTable.propTypes = {
  config: PropTypes.object.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  status: PropTypes.oneOf(['loading', 'error', 'ready']),
  onRetry: PropTypes.func,
  load: PropTypes.func,
  controlledState: PropTypes.object,
  rowActions: PropTypes.func,
  renderDetail: PropTypes.func,
  // expanded rows show child rows in the table's own columns instead of a detail (rows/ChildRows.js)
  childRows: PropTypes.shape({
    load: PropTypes.func.isRequired,
    noun: PropTypes.string,
    emptyText: PropTypes.string,
  }),
  headerActions: PropTypes.oneOfType([
    PropTypes.arrayOf(actionShape),
    PropTypes.node,
  ]),
  consumerRegion: PropTypes.node,
  onStateChange: PropTypes.func,
}
